if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.upsellReady) return;
      this.upsellReady = true;

      this.addEventListener('change', (event) => {
        // Stop the cart's own quantity "change" handler from seeing our dropdown.
        event.stopPropagation();
        const select = event.target.closest('.cart-upsell__select');
        if (select) this.onSelectChange(select);
      });

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.cart-upsell__button');
        if (!button) return;
        event.preventDefault();
        this.addToCart(button);
      });

      if (this.dataset.recsUrl) this.loadRecommendations();
    }

    fetchHtml(url) {
      window.cartUpsellCache = window.cartUpsellCache || {};
      const cache = window.cartUpsellCache;
      if (cache[url]) return Promise.resolve(cache[url]);

      return fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          if (text) cache[url] = text;
          return text;
        })
        .catch(() => '');
    }

    parseSection(html) {
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      const recs = doc.querySelector('.cart-upsell-recs');
      if (!recs) return { header: null, cards: [] };
      return {
        header: recs.querySelector('.cart-upsell__header'),
        cards: [...recs.querySelectorAll('.cart-upsell__card')],
      };
    }

    loadRecommendations() {
      const recsUrl = `${this.dataset.recsUrl}&product_id=${this.dataset.productId}`;
      const topupUrl = this.dataset.topupUrl || '';
      const seedGenders = (this.dataset.genders || ',').split(',').filter(Boolean);
      const inCart = this.dataset.cartProducts || ',';
      const max = parseInt(this.dataset.max, 10) || 3;

      const notInCart = (card) => !inCart.includes(`,${card.dataset.productId},`);
      const sameGender = (card) =>
        !seedGenders.length || seedGenders.some((gender) => (card.dataset.genders || '').includes(`,${gender},`));

      this.fetchHtml(recsUrl)
        .then((recsHtml) => {
          const recs = this.parseSection(recsHtml);
          const chosen = recs.cards.filter((card) => notInCart(card) && sameGender(card)).slice(0, max);

          if (chosen.length >= max || !topupUrl || !seedGenders.length) {
            return { chosen, header: recs.header };
          }

          // Not enough same-gender related products: top up from the gender collection itself.
          return this.fetchHtml(topupUrl).then((topHtml) => {
            const top = this.parseSection(topHtml);
            const have = new Set(chosen.map((card) => card.dataset.productId));
            top.cards.forEach((card) => {
              if (chosen.length < max && !have.has(card.dataset.productId) && notInCart(card)) {
                chosen.push(card);
                have.add(card.dataset.productId);
              }
            });
            return { chosen, header: recs.header || top.header };
          });
        })
        .then(({ chosen, header }) => this.renderCards(chosen, header))
        .catch(() => this.remove());
    }

    renderCards(cards, header) {
      if (!cards.length || !header) {
        this.remove();
        return;
      }

      const list = document.createElement('ul');
      list.className = 'cart-upsell__list';
      list.setAttribute('role', 'list');
      cards.forEach((card) => list.appendChild(document.importNode(card, true)));

      this.dataset.count = String(cards.length);
      this.textContent = '';
      this.appendChild(document.importNode(header, true));
      this.appendChild(list);
      this.hidden = false;
    }

    onSelectChange(select) {
      const card = select.closest('.cart-upsell__card');
      const button = card.querySelector('.cart-upsell__button');
      const option = select.options[select.selectedIndex];

      button.disabled = !select.value;
      this.hideError(card);

      const price = card.querySelector('.cart-upsell__price');
      const compare = card.querySelector('.cart-upsell__compare');
      if (price && option && option.dataset.price) price.textContent = option.dataset.price;
      if (compare && option) {
        const compareText = option.dataset.compare || '';
        compare.textContent = compareText;
        compare.hidden = !compareText;
      }
    }

    showError(card, message) {
      const error = card.querySelector('.cart-upsell__error');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    hideError(card) {
      const error = card.querySelector('.cart-upsell__error');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    addToCart(button) {
      const card = button.closest('.cart-upsell__card');
      const select = card.querySelector('.cart-upsell__select');
      const variantId = select ? select.value : card.dataset.variantId;

      if (!variantId) {
        this.showError(card, 'Please choose an option first.');
        return;
      }

      const drawer = this.closest('cart-drawer');
      const body = { id: Number(variantId), quantity: 1 };

      if (drawer) {
        body.sections = drawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
        body.sections_url = window.location.pathname;
        drawer.setActiveElement(button);
      }

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding...';
      card.classList.add('is-loading');
      this.hideError(card);

      const restore = () => {
        button.textContent = originalLabel;
        button.disabled = select ? !select.value : false;
        card.classList.remove('is-loading');
      };

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) {
            this.showError(card, state.description || state.message || 'Could not add this item.');
            restore();
            return;
          }

          if (drawer) {
            drawer.renderContents(state);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-upsell',
                productVariantId: variantId,
                cartData: state,
              });
            }
          } else {
            window.location.reload();
          }
        })
        .catch(() => {
          this.showError(card, 'Something went wrong. Please try again.');
          restore();
        });
    }
  }

  customElements.define('cart-upsell', CartUpsell);
}